import { useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import printService from '@/services/printService';
import soundService from '@/services/soundService';

const WS_URL = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');

// WebSocket path needs /api prefix for proper routing through ingress
const getWsUrl = (storeId) => {
  if (!WS_URL || !storeId) return null;
  return `${WS_URL}/api/ws/notifications/${storeId}`;
};

export const useOrderNotifications = (storeId, onNewOrder, autoPrintEnabled = false, printConfig = {}, notificationConfig = {}) => {
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 10;
  const isConnecting = useRef(false);
  
  // Use refs for callbacks to avoid reconnection on callback changes
  const onNewOrderRef = useRef(onNewOrder);
  const notificationConfigRef = useRef(notificationConfig);
  
  // Update refs when props change
  useEffect(() => {
    onNewOrderRef.current = onNewOrder;
  }, [onNewOrder]);
  
  useEffect(() => {
    notificationConfigRef.current = notificationConfig;
  }, [notificationConfig]);

  // Set sound volume from config
  useEffect(() => {
    if (notificationConfig.volume !== undefined) {
      soundService.setVolume(notificationConfig.volume);
    }
  }, [notificationConfig.volume]);

  const playNotificationSound = useCallback(() => {
    const config = notificationConfigRef.current;
    if (config.sound_enabled !== false) {
      const soundType = config.sound_type || 'default';
      soundService.play(soundType);
    }
  }, []);

  // Simplified print function using the new printService
  const printOrder = useCallback(async (orderData, format = 'thermal') => {
    try {
      console.log('[AUTO-PRINT] Calling printService.print for order:', orderData.order_number);
      const result = await printService.print(orderData);
      
      if (result.success) {
        toast.success(`Pedido #${orderData.order_number} enviado para impressao!`);
      } else {
        // Fallback: open print window
        console.warn('Print-js failed, opening print window');
        const opened = printService.openPrintWindow(orderData);
        if (opened) {
          toast.info(`Janela de impressao aberta para pedido #${orderData.order_number}`);
        } else {
          toast.error('Nao foi possivel abrir a janela de impressao. Verifique o bloqueador de popups.');
        }
      }
    } catch (e) {
      console.error('Print failed:', e);
      toast.error('Erro na impressao. Tente imprimir manualmente.');
    }
  }, []);

  // Connect to WebSocket - only depends on storeId
  useEffect(() => {
    const wsUrl = getWsUrl(storeId);
    if (!wsUrl) {
      console.log('[WS] Cannot connect - missing storeId or WS_URL, storeId:', storeId);
      return;
    }

    // Prevent multiple connections
    if (isConnecting.current || wsRef.current?.readyState === WebSocket.OPEN) {
      console.log('[WS] Already connected or connecting');
      return;
    }

    const connect = () => {
      if (isConnecting.current) return;
      isConnecting.current = true;

      try {
        console.log('[WS] Connecting to:', wsUrl);
        const ws = new WebSocket(wsUrl);

        ws.onopen = () => {
          console.log('[WS] Connected successfully!');
          reconnectAttempts.current = 0;
          isConnecting.current = false;
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            
            // Ignore pong messages
            if (data === 'pong' || event.data === 'pong') return;
            
            console.log('[AUTO-PRINT] WebSocket message received:', data.type);
            
            if (data.type === 'new_order') {
              console.log('[AUTO-PRINT] New order received:', data.order_number, 'auto_print:', data.auto_print);
              
              // Notify callback using ref
              if (onNewOrderRef.current) {
                onNewOrderRef.current(data);
              }
              
              // Show toast notification
              toast.success(`Novo pedido #${data.order_number}!`, {
                description: `${data.customer_name} - R$ ${data.total.toFixed(2)}`,
                duration: 10000
              });
              
              // Play notification sound
              playNotificationSound();
              
              // NOTE: Auto print via browser is DISABLED
              // Browser security prevents silent printing - it ALWAYS shows a dialog
              // Auto print should be handled by the DESKTOP APPLICATION (PowerShell)
              // The desktop app polls for new orders and prints directly to the printer
              if (data.auto_print) {
                console.log('[AUTO-PRINT] Auto-print flag received, but browser printing is disabled.');
                console.log('[AUTO-PRINT] Use the ADEGGA Desktop App for automatic printing without dialogs.');
                // Show a hint to use the desktop app if not already
                toast.info(`Novo pedido! Use o App de Impressao para imprimir automaticamente.`, {
                  duration: 5000
                });
              }
            }
          } catch (e) {
            console.error('Error parsing WebSocket message:', e);
          }
        };

        ws.onclose = (event) => {
          console.log('[WS] Disconnected, code:', event.code);
          isConnecting.current = false;
          wsRef.current = null;
          
          // Attempt to reconnect if not a normal close
          if (event.code !== 1000 && reconnectAttempts.current < maxReconnectAttempts) {
            reconnectAttempts.current++;
            const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
            console.log(`[WS] Reconnecting in ${delay}ms (attempt ${reconnectAttempts.current})`);
            reconnectTimeoutRef.current = setTimeout(connect, delay);
          }
        };

        ws.onerror = (error) => {
          console.error('[WS] Error:', error);
          isConnecting.current = false;
        };

        wsRef.current = ws;
      } catch (e) {
        console.error('[WS] Connection error:', e);
        isConnecting.current = false;
      }
    };

    connect();

    // Heartbeat to keep connection alive
    const heartbeat = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send('ping');
      }
    }, 25000);

    // Cleanup
    return () => {
      clearInterval(heartbeat);
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmounting');
        wsRef.current = null;
      }
      isConnecting.current = false;
    };
  }, [storeId, playNotificationSound, printOrder]); // Only reconnect when storeId changes

  return { printOrder, playNotificationSound };
};
